/**
 * jc-variant-picker.js
 * バリアント選択UI — 内容量ピル + 包装カード + 温度帯カード（動的検出） + 動的価格 + 送料バッジ
 *
 * バリアント解決はオプション数Nの汎用マッチング（variant.options 配列 × 選択状態）。
 * 温度帯軸（temperatureOptionIndex）が無い商品では従来の2軸挙動と完全に同一。
 */

class JCVariantPicker {
  constructor(container) {
    this.container = container;
    this.sectionId = container.dataset.sectionId;
    this.data = JSON.parse(
      document.getElementById(`jc-variant-data-${this.sectionId}`).textContent
    );
    this.variants = this.data.variants.filter(v => v.option1 !== null && v.option1 !== '');
    this.volumeIndex = this.data.volumeOptionIndex;
    this.packagingIndex = this.data.packagingOptionIndex;
    this.temperatureIndex = this.data.temperatureOptionIndex ?? -1;

    // DOM refs
    this.pills = container.querySelectorAll('.jc-vp__pill');
    this.cards = container.querySelectorAll('.jc-vp__card');
    this.tempCards = container.querySelectorAll('.jc-vp__tempcard');
    this.tempNoteEl = container.querySelector('[data-temp-note]');
    this.noticeEl = container.querySelector('[data-temp-notice]');
    this.toastEl = container.querySelector('[data-temp-toast]');
    this.priceEl = container.querySelector('[data-price]');
    this.priceTaxEl = container.querySelector('.jc-vp__price-tax');
    this.shippingBadgeEl = container.querySelector('[data-shipping-badge]');
    this.summaryEl = container.querySelector('[data-price-summary]');
    this.priceArea = container.querySelector('[data-price-area]');

    // product form の hidden input（Dawn標準）
    this.productForm = document.querySelector(`product-form[data-section="${this.sectionId}"]`)
      || document.querySelector('product-form');
    this.variantIdInput = this.productForm?.querySelector('input[name="id"]');

    // State
    this.selectedVolume = null;
    this.selectedPackaging = null;
    this.selectedTemperature = null;
    this.toastTimer = null;

    this.bindEvents();
    this.initDefaultState();
  }

  /** variant のオプション値をインデックスで取得（options 配列が無い旧データは option1/2 にフォールバック） */
  getOpt(variant, index) {
    if (index < 0) return null;
    if (Array.isArray(variant.options)) return variant.options[index];
    return index === 0 ? variant.option1 : variant.option2;
  }

  initDefaultState() {
    const urlParams = new URLSearchParams(window.location.search);
    const urlVariantId = urlParams.get('variant');

    if (urlVariantId) {
      const variant = this.variants.find(v => v.id === parseInt(urlVariantId));
      if (variant) {
        if (this.volumeIndex >= 0) this.selectVolume(this.getOpt(variant, this.volumeIndex), false);
        if (this.temperatureIndex >= 0) this.selectTemperature(this.getOpt(variant, this.temperatureIndex), false);
        if (this.packagingIndex >= 0) this.selectPackaging(this.getOpt(variant, this.packagingIndex), false);
        return;
      }
    }

    // URL指定なし → デフォルト選択
    if (this.volumeIndex >= 0 && this.pills.length > 0) {
      const firstAvailablePill = [...this.pills].find(p => !p.disabled);
      if (firstAvailablePill) {
        this.selectVolume(firstAvailablePill.dataset.optionValue, false);
      }
    }

    // 温度帯 = 「冷凍」（非チルドカード）をプリセレクト
    if (this.temperatureIndex >= 0 && this.tempCards.length > 0) {
      const frozenCard = [...this.tempCards].find(c => c.dataset.chilled !== 'true') || this.tempCards[0];
      this.selectTemperature(frozenCard.dataset.optionValue, false);
    }

    if (this.packagingIndex >= 0) {
      if (this.cards.length === 1) {
        this.selectPackaging(this.cards[0].dataset.optionValue, false);
      } else {
        // 包装 = 未選択スタート（明示的選択を強制）
        this.selectedPackaging = null;
        this.showPriceRange();
        this.disableCartButton();
      }
    }

    // 内容量のみの1軸商品 → 最初のピル選択で確定
    if (this.volumeIndex >= 0 && this.packagingIndex < 0) {
      if (this.selectedVolume) {
        const variant = this.findVariant();
        if (variant) this.applyVariant(variant);
      }
    }

    // 包装のみの1軸商品で選択肢1つ → 自動確定
    if (this.volumeIndex < 0 && this.packagingIndex >= 0 && this.cards.length === 1) {
      const variant = this.findVariant();
      if (variant) this.applyVariant(variant);
    }
  }

  selectVolume(value, updateUI = true) {
    this.selectedVolume = value;
    this.hideToast();

    this.pills.forEach(pill => {
      pill.classList.toggle('jc-vp__pill--active', pill.dataset.optionValue === value);
    });

    if (this.temperatureIndex >= 0) {
      this.updateTemperatureAvailability();
    }

    if (this.packagingIndex >= 0) {
      this.updatePackagingAvailability();
      this.updateSavingDisplay();
    }

    if (this.selectedPackaging) {
      const variant = this.findVariant();
      if (variant) this.applyVariant(variant);
    } else if (this.packagingIndex >= 0) {
      this.showPriceRange();
      this.disableCartButton();
    } else {
      const variant = this.findVariant();
      if (variant) this.applyVariant(variant);
    }
  }

  selectPackaging(value, updateUI = true) {
    this.selectedPackaging = value;
    this.hideToast();

    this.cards.forEach(card => {
      card.classList.toggle('jc-vp__card--active', card.dataset.optionValue === value);
    });

    const variant = this.findVariant();
    if (variant) {
      this.applyVariant(variant);
    }
  }

  selectTemperature(value, updateUI = true) {
    this.selectedTemperature = value;
    this.hideToast();

    let isChilled = false;
    this.tempCards.forEach(card => {
      const active = card.dataset.optionValue === value;
      card.classList.toggle('jc-vp__tempcard--active', active);
      if (active && card.dataset.chilled === 'true') isChilled = true;
    });

    this.toggleChilledNotice(isChilled);

    // 温度帯によって包装カードの在庫状況が変わりうる
    if (this.packagingIndex >= 0 && this.selectedVolume) {
      this.updatePackagingAvailability();
    }

    if (this.packagingIndex < 0 || this.selectedPackaging) {
      const variant = this.findVariant();
      if (variant) this.applyVariant(variant);
    }
  }

  /**
   * disabled温度帯カード（存在しない組合せ）タップ時の挙動。
   * 現方式: トースト確認（画面下部固定・6秒自動消滅）。将来変更はこの関数内で完結させる。
   */
  handleDisabledTemperatureTap(card) {
    if (!this.toastEl) return;
    const targetValue = card.dataset.optionValue;

    this.showToast();

    const switchBtn = this.toastEl.querySelector('[data-temp-toast-switch]');
    if (switchBtn) {
      switchBtn.onclick = () => {
        this.hideToast();
        // この温度帯が存在する最初の内容量（=20g想定）を動的に特定して同時選択
        const targetVolume = this.findFirstVolumeFor(targetValue);
        if (targetVolume) {
          this.selectVolume(targetVolume);
          this.selectTemperature(targetValue);
        }
      };
    }
  }

  /** 指定温度帯値のバリアントが存在する最初の内容量値を返す */
  findFirstVolumeFor(temperatureValue) {
    if (this.volumeIndex < 0) return null;
    const match = this.variants.find(
      v => this.getOpt(v, this.temperatureIndex) === temperatureValue
    );
    return match ? this.getOpt(match, this.volumeIndex) : null;
  }

  showToast() {
    if (!this.toastEl) return;
    clearTimeout(this.toastTimer);
    this.toastEl.hidden = false;
    // reflow を挟んでフェードイン用クラスを付与
    void this.toastEl.offsetWidth;
    this.toastEl.classList.add('jc-temp-toast--visible');
    this.toastTimer = setTimeout(() => this.hideToast(), 6000);
  }

  hideToast() {
    if (!this.toastEl || this.toastEl.hidden) return;
    clearTimeout(this.toastTimer);
    this.toastEl.classList.remove('jc-temp-toast--visible');
    this.toastEl.hidden = true;
  }

  /** 冷蔵選択時の注意ブロック表示切替（モーションはCSS側で定義） */
  toggleChilledNotice(show) {
    if (!this.noticeEl) return;
    const wasOpen = this.noticeEl.classList.contains('jc-temp-notice--open');

    if (show && !wasOpen) {
      this.noticeEl.classList.add('jc-temp-notice--open', 'jc-temp-notice--flash');
      clearTimeout(this.noticeFlashTimer);
      this.noticeFlashTimer = setTimeout(() => {
        this.noticeEl.classList.remove('jc-temp-notice--flash');
      }, 650);
      // 展開完了後、ブロック下端がビューポート外の場合のみスクロール
      setTimeout(() => {
        const rect = this.noticeEl.getBoundingClientRect();
        if (rect.bottom > window.innerHeight) {
          this.noticeEl.scrollIntoView({ block: 'nearest' });
        }
      }, 280);
    } else if (!show && wasOpen) {
      this.noticeEl.classList.remove('jc-temp-notice--open', 'jc-temp-notice--flash');
    }
  }

  /** 温度帯カードの disabled / soldout 状態と20g注記の表示を更新 */
  updateTemperatureAvailability() {
    if (this.temperatureIndex < 0) return;

    let chilledOffered = false;

    this.tempCards.forEach(card => {
      const val = card.dataset.optionValue;
      const combos = this.volumeIndex < 0
        ? this.variants.filter(v => this.getOpt(v, this.temperatureIndex) === val)
        : this.variants.filter(
            v => this.getOpt(v, this.volumeIndex) === this.selectedVolume
              && this.getOpt(v, this.temperatureIndex) === val
          );
      const notOffered = combos.length === 0;
      const soldOut = !notOffered && !combos.some(v => v.available);

      card.classList.toggle('jc-vp__tempcard--disabled', notOffered);
      card.classList.toggle('jc-vp__tempcard--soldout', soldOut);
      card.dataset.notOffered = notOffered;
      // notOffered はタップでトーストを出すため disabled 属性にしない
      card.disabled = soldOut;
      card.setAttribute('aria-disabled', notOffered || soldOut);

      if (card.dataset.chilled === 'true' && !notOffered) {
        chilledOffered = true;
      }
    });

    // 注記「※冷蔵は20gのみお選びいただけます」: 冷蔵が選択可能な内容量でのみ表示
    // （disabled時はカード内文言に役割移譲・二重表示禁止）
    if (this.tempNoteEl) {
      this.tempNoteEl.hidden = !chilledOffered;
    }

    // 現在選択中の温度帯がこの内容量で存在しない → 最初の有効カードへ自動切替
    if (this.selectedTemperature) {
      const currentCard = [...this.tempCards].find(
        c => c.dataset.optionValue === this.selectedTemperature
      );
      if (currentCard && currentCard.dataset.notOffered === 'true') {
        const fallbackCard = [...this.tempCards].find(c => c.dataset.notOffered !== 'true');
        if (fallbackCard) {
          this.selectTemperature(fallbackCard.dataset.optionValue, false);
        }
      }
    }
  }

  findVariant() {
    const selections = [];
    if (this.volumeIndex >= 0) selections.push([this.volumeIndex, this.selectedVolume]);
    if (this.packagingIndex >= 0) selections.push([this.packagingIndex, this.selectedPackaging]);
    if (this.temperatureIndex >= 0) selections.push([this.temperatureIndex, this.selectedTemperature]);
    if (selections.length === 0) return undefined;

    return this.variants.find(v =>
      selections.every(([index, value]) => this.getOpt(v, index) === value)
    );
  }

  applyVariant(variant) {
    // バリアント切替時のスクロールジャンプ防止：
    // Dawn の media-gallery.setActiveMedia() が setTimeout 経由で
    // window.scrollTo を呼ぶため、rAF と setTimeout の両方で復元する。
    const savedScrollY = window.scrollY;
    const restoreScroll = () => window.scrollTo(0, savedScrollY);
    requestAnimationFrame(() => {
      requestAnimationFrame(restoreScroll);
    });
    setTimeout(restoreScroll, 0);
    setTimeout(restoreScroll, 50);

    if (this.variantIdInput) {
      this.variantIdInput.value = variant.id;
      this.variantIdInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    this.priceEl.textContent = this.formatPrice(variant.price);
    this.priceTaxEl.style.display = '';
    this.priceArea.classList.remove('jc-vp__price-area--range', 'jc-vp__price-area--pending');

    this.updateShippingBadge(variant);

    // SKU更新
    const skuDisplay = document.querySelector('[data-sku-display]');
    if (skuDisplay) {
      const skuValue = skuDisplay.querySelector('[data-sku-value]');
      if (variant.sku) {
        skuValue.textContent = variant.sku;
        skuDisplay.style.display = '';
      } else {
        skuDisplay.style.display = 'none';
      }
    }

    const parts = [];
    if (this.selectedVolume) parts.push(this.selectedVolume);
    if (this.selectedPackaging) {
      if (this.selectedPackaging.includes('化粧箱')) {
        parts.push('贈答用化粧箱 シェルスプーン付');
      } else {
        parts.push('クリアケース');
      }
    }
    if (this.temperatureIndex >= 0 && this.selectedTemperature) {
      parts.push(this.selectedTemperature);
    }
    this.summaryEl.textContent = parts.join(' ／ ');

    if (variant.available) {
      this.enableCartButton();
    } else {
      this.disableCartButton(true); // true = soldOut
    }

    const url = new URL(window.location);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);

    this.switchMediaGallery(variant);
    this.dispatchDawnVariantChange(variant);

    document.dispatchEvent(new CustomEvent('jc:price-rendered', {
      detail: { variantId: variant.id, sectionId: this.sectionId },
    }));
  }

  switchMediaGallery(variant) {
    if (!variant.featuredMediaId) return;
    const mediaId = `${this.sectionId}-${variant.featuredMediaId}`;
    const mediaGallery = document.querySelector('media-gallery');
    if (mediaGallery) {
      mediaGallery.setActiveMedia(mediaId, true);
    }
  }

  showPriceRange() {
    if (!this.selectedVolume) return;

    const matching = this.variants.filter(v =>
      this.getOpt(v, this.volumeIndex) === this.selectedVolume
      && v.available
      && (this.temperatureIndex < 0 || !this.selectedTemperature
        || this.getOpt(v, this.temperatureIndex) === this.selectedTemperature)
    );
    if (matching.length === 0) return;

    const prices = matching.map(v => v.price);
    const min = Math.min(...prices);
    const max = Math.max(...prices);

    if (min === max) {
      this.priceEl.textContent = this.formatPrice(min);
    } else {
      this.priceEl.textContent = `${this.formatPrice(min)}〜${this.formatPrice(max)}`;
    }

    this.priceTaxEl.style.display = '';
    this.priceArea.classList.add('jc-vp__price-area--range');
    this.priceArea.classList.remove('jc-vp__price-area--pending');
    this.summaryEl.textContent = '包装を選択してください';

    // 未選択/範囲表示中はSKU非表示
    const skuDisplay = document.querySelector('[data-sku-display]');
    if (skuDisplay) {
      skuDisplay.style.display = 'none';
    }

    this.updateShippingBadgeForRange(matching);

    document.dispatchEvent(new CustomEvent('jc:price-rendered', {
      detail: { variantId: null, sectionId: this.sectionId },
    }));
  }

  updateShippingBadge(variant) {
    // Liquid側で true/false/null に正規化済み
    let isFree;
    if (variant.freeShipping !== null) {
      isFree = variant.freeShipping === true;
    } else if (this.data.productFreeShipping !== null) {
      isFree = this.data.productFreeShipping === true;
    } else {
      isFree = false;
    }

    if (isFree) {
      this.shippingBadgeEl.textContent = '送料無料';
      this.shippingBadgeEl.className = 'jc-vp__shipping-badge jc-vp__shipping-badge--free';
    } else {
      this.shippingBadgeEl.textContent = '+送料';
      this.shippingBadgeEl.className = 'jc-vp__shipping-badge jc-vp__shipping-badge--paid';
    }
    this.shippingBadgeEl.style.display = '';
  }

  updateShippingBadgeForRange(matchingVariants) {
    const freeStatuses = matchingVariants.map(v => {
      if (v.freeShipping !== null) {
        return v.freeShipping === true;
      }
      if (this.data.productFreeShipping !== null) {
        return this.data.productFreeShipping === true;
      }
      return false;
    });

    const allFree = freeStatuses.every(s => s);
    const allPaid = freeStatuses.every(s => !s);

    if (allFree) {
      this.shippingBadgeEl.textContent = '送料無料';
      this.shippingBadgeEl.className = 'jc-vp__shipping-badge jc-vp__shipping-badge--free';
      this.shippingBadgeEl.style.display = '';
    } else if (allPaid) {
      this.shippingBadgeEl.textContent = '+送料';
      this.shippingBadgeEl.className = 'jc-vp__shipping-badge jc-vp__shipping-badge--paid';
      this.shippingBadgeEl.style.display = '';
    } else {
      this.shippingBadgeEl.style.display = 'none';
    }
  }

  updateSavingDisplay() {
    if (!this.selectedVolume || this.packagingIndex < 0) return;

    const matching = this.variants.filter(v =>
      this.getOpt(v, this.volumeIndex) === this.selectedVolume
      && v.available
      && (this.temperatureIndex < 0 || !this.selectedTemperature
        || this.getOpt(v, this.temperatureIndex) === this.selectedTemperature)
    );

    const giftVariant = matching.find(v => this.getOpt(v, this.packagingIndex)?.includes('化粧箱'));
    const clearVariant = matching.find(v => !this.getOpt(v, this.packagingIndex)?.includes('化粧箱'));

    if (giftVariant && clearVariant) {
      const saving = giftVariant.price - clearVariant.price;
      if (saving > 0) {
        const savingEls = this.container.querySelectorAll('[data-saving-el]');
        savingEls.forEach(el => {
          el.textContent = `${this.formatPrice(saving)} お得`;
        });
      }
    }
  }

  updatePackagingAvailability() {
    if (!this.selectedVolume) return;

    this.cards.forEach(card => {
      const pkgVal = card.dataset.optionValue;
      const variant = this.variants.find(v =>
        this.getOpt(v, this.volumeIndex) === this.selectedVolume
        && this.getOpt(v, this.packagingIndex) === pkgVal
        && (this.temperatureIndex < 0 || !this.selectedTemperature
          || this.getOpt(v, this.temperatureIndex) === this.selectedTemperature)
      );
      const isAvailable = variant?.available ?? false;
      card.classList.toggle('jc-vp__card--soldout', !isAvailable);
      card.disabled = !isAvailable;
      card.setAttribute('aria-disabled', !isAvailable);
    });
  }

  getCartButton() {
    // productForm が <product-form> 要素の場合は内部に submit ボタンが無く、
    // .jc-blk4__add-btn は form="..." 属性で紐付く（DOM外）。
    // document 全体から探してフォールバックする。
    return this.productForm?.querySelector('[name="add"]')
      || this.productForm?.querySelector('button[type="submit"]')
      || document.querySelector('.jc-blk4__add-btn')
      || document.querySelector(`form[id="product-form-${this.sectionId}"] [name="add"]`);
  }

  getStickyButton() {
    return document.querySelector('[data-sticky-add-to-cart]');
  }

  enableCartButton() {
    const btn = this.getCartButton();
    if (btn) {
      btn.disabled = false;
      btn.classList.remove('jc-vp__cart-disabled');
      btn.classList.remove('jc-vp__cart-soldout');
      const textEl = btn.querySelector('.jc-vp__cart-text') || btn.querySelector('span') || btn;
      textEl.textContent = 'カートに入れる';
    }
    const stickyBtn = this.getStickyButton();
    if (stickyBtn) {
      stickyBtn.disabled = false;
      stickyBtn.classList.remove('sticky-cart__button--disabled');
      stickyBtn.classList.remove('sticky-cart__button--soldout');
      const stickyTextEl = stickyBtn.querySelector('.jc-vp__cart-text') || stickyBtn;
      stickyTextEl.textContent = 'カートに入れる';
    }
  }

  disableCartButton(soldOut = false) {
    const btn = this.getCartButton();
    if (btn) {
      btn.disabled = true;
      btn.classList.add('jc-vp__cart-disabled');
      if (soldOut) {
        btn.classList.add('jc-vp__cart-soldout');
        const textEl = btn.querySelector('.jc-vp__cart-text') || btn.querySelector('span') || btn;
        textEl.textContent = '完売しました';
      } else {
        btn.classList.remove('jc-vp__cart-soldout');
      }
    }
    const stickyBtn = this.getStickyButton();
    if (stickyBtn) {
      stickyBtn.disabled = true;
      stickyBtn.classList.add('sticky-cart__button--disabled');
      if (soldOut) {
        stickyBtn.classList.add('sticky-cart__button--soldout');
        const stickyTextEl = stickyBtn.querySelector('.jc-vp__cart-text') || stickyBtn;
        stickyTextEl.textContent = '完売しました';
      } else {
        stickyBtn.classList.remove('sticky-cart__button--soldout');
        const stickyTextEl = stickyBtn.querySelector('.jc-vp__cart-text') || stickyBtn;
        stickyTextEl.textContent = 'カートに入れる';
      }
    }
  }

  dispatchDawnVariantChange(variant) {
    const dawnPicker = document.querySelector(
      `variant-selects[data-section="${this.sectionId}"], variant-radios[data-section="${this.sectionId}"]`
    );

    if (dawnPicker) {
      const selects = dawnPicker.querySelectorAll('select');
      const selections = [
        [this.volumeIndex, this.selectedVolume],
        [this.packagingIndex, this.selectedPackaging],
        [this.temperatureIndex, this.selectedTemperature],
      ];
      selections.forEach(([index, value]) => {
        if (index >= 0 && value && selects.length > index) {
          selects[index].value = value;
        }
      });
      selects.forEach(s => s.dispatchEvent(new Event('change', { bubbles: true })));
    }
  }

  formatPrice(cents) {
    return `¥${(cents / 100).toLocaleString()}`;
  }

  bindEvents() {
    this.pills.forEach(pill => {
      pill.addEventListener('click', () => {
        if (pill.disabled) return;
        this.selectVolume(pill.dataset.optionValue);
      });
    });

    this.cards.forEach(card => {
      card.addEventListener('click', () => {
        if (card.disabled) return;
        this.selectPackaging(card.dataset.optionValue);
      });
    });

    this.tempCards.forEach(card => {
      card.addEventListener('click', () => {
        if (card.dataset.notOffered === 'true') {
          this.handleDisabledTemperatureTap(card);
          return;
        }
        if (card.disabled) return;
        this.selectTemperature(card.dataset.optionValue);
      });
    });

    if (this.toastEl) {
      const closeBtn = this.toastEl.querySelector('[data-temp-toast-close]');
      if (closeBtn) {
        closeBtn.addEventListener('click', () => this.hideToast());
      }
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.jc-variant-picker:not(.jc-variant-picker--single)').forEach(el => {
    new JCVariantPicker(el);
  });
});
